'use client';

/**
 * Site-wide analytics shim and first-touch attribution.
 *
 * Until now the only instrumented surface on the site was `/compare`. The demo
 * form — the site's actual conversion, and the destination of 199 CTAs — fired
 * nothing at all. HubSpot received the lead server-side, so sales never missed
 * one, but GA4 had no key event, PostHog had no funnel, and no ad platform
 * could see a conversion to optimise against. The conversion rate of the site
 * was, literally, an unknown number.
 *
 * Events fan out to both PostHog and the GTM/GA4 dataLayer, and no-op when
 * neither is present — which is the normal state before cookie consent, since
 * every tag is consent-gated. Nothing here writes a cookie or reads one until
 * the visitor's consent allows analytics.
 */

import { hasAnalyticsConsent } from './consent';

type Props = Record<string, unknown>;

interface AnalyticsWindow {
  posthog?: { capture: (event: string, props?: Props) => void };
  dataLayer?: Props[];
  gtag?: (command: 'event', name: string, params?: Props) => void;
}

/** First-touch attribution, stored for the life of the browsing session. */
export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  li_fat_id?: string;
  /** The external page that sent them, if any. */
  referrer?: string;
  /** The first page on open.cx they saw. */
  landing_page?: string;
}

const STORAGE_KEY = 'ocx_attribution';

const PARAM_KEYS: Array<keyof Attribution> = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'li_fat_id',
];

/**
 * Emit an event to every analytics destination that is loaded.
 *
 * Never throws: analytics failing is not a reason for a page or a form to
 * break, and this runs inside the demo submit path.
 */
export function track(event: string, props: Props = {}): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as AnalyticsWindow;
  try {
    w.posthog?.capture(event, props);

    // GA4, directly. This site loads gtag.js as well as GTM, and the two
    // share a dataLayer — but they read it differently. gtag only registers
    // what arrives through `gtag('event', ...)`; a plain `{ event: ... }`
    // object pushed onto the queue is invisible to it. So the dataLayer push
    // below reaches GTM and nothing else, and GA4 saw no conversions at all
    // until this call existed.
    w.gtag?.('event', event, props);

    // GTM, for anyone building triggers on the dataLayer. This is the only
    // path that existed before, which is why `generate_lead` never showed up
    // in GA4's event list to be marked a key event.
    //
    // If a GA4 tag is ever wired to these same event names inside GTM, remove
    // one of the two paths — otherwise every conversion is counted twice.
    if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event, ...props });
  } catch {
    // Analytics must never break the page.
  }
}

/**
 * Record first-touch attribution, once per session.
 *
 * First-touch rather than last: the campaign that first brought someone to the
 * site is the one that earned the lead, and a visitor who returns via a direct
 * visit before converting should not have that credited to "direct".
 *
 * Stored in sessionStorage, not a cookie — it never needs to reach the server
 * on every request, and keeping it out of the cookie jar keeps it out of the
 * HubSpot/GTM payloads that read cookies.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;
  // Same consent gate the server uses before forwarding HubSpot's hutk —
  // `hasAnalyticsConsent` reads a cookie header, and document.cookie is one.
  if (!hasAnalyticsConsent(document.cookie)) return;

  try {
    if (window.sessionStorage.getItem(STORAGE_KEY)) return; // first touch wins

    const params = new URLSearchParams(window.location.search);
    const attribution: Attribution = {};

    for (const key of PARAM_KEYS) {
      const value = params.get(key);
      if (value) attribution[key] = value.slice(0, 200);
    }

    const ref = document.referrer;
    if (ref && !ref.includes(window.location.host)) {
      attribution.referrer = ref.slice(0, 500);
    }
    attribution.landing_page = window.location.pathname.slice(0, 500);

    // A landing page alone is not attribution worth storing — without it,
    // "direct" and "we captured nothing" stay distinguishable.
    const hasSignal = Object.keys(attribution).some(
      (k) => k !== 'landing_page',
    );
    if (!hasSignal) return;

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // sessionStorage throws in private-mode Safari and when storage is full.
  }
}

/** Read back whatever attribution was captured this session. */
export function getAttribution(): Attribution {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : {};
  } catch {
    return {};
  }
}
